/**
 * Static placeholder content for the booking landing page.
 *
 * This is the SINGLE swap seam (see docs/booking-landing-page/03-frontend-design.md).
 * `FeaturedStayDTO` mirrors the future `GET /v1/public/featured` response field-for-field
 * (docs/booking-landing-page/02-backend-design.md), so when that endpoint ships the only
 * change is replacing these exported datasets with a TanStack Query hook returning the
 * same shape — no component changes required.
 *
 * Everything here is curated marketing data. Ratings/figures/beds/baths/sqft are static
 * placeholders (the underlying schema fields don't exist yet). Content is India-first.
 */

export type StayCategory = 'all' | 'apartment' | 'resort' | 'lodge' | 'hotel';

export interface FeaturedStayDTO {
  id: string; // → /hotel/:id
  name: string;
  location: string; // "City, State" (text only, no maps)
  imageUrl: string; // primary card image
  images: string[]; // gallery — featured thumbnail strip
  guests: number;
  beds: number; // placeholder until a real field exists
  baths: number; // placeholder until a real field exists
  areaSqft: number; // placeholder until a real field exists
  pricePerNight: number; // "from" price
  currency: string; // property-driven (INR)
  rating: number; // static (reviews table unbuilt)
  reviewCount: number; // static
  category: Exclude<StayCategory, 'all'>; // pill filter key (cosmetic)
}

export interface PillCategory {
  key: StayCategory;
  label: string;
}

export interface FeatureItem {
  id: string;
  icon: 'sparkles' | 'badge-dollar' | 'shield-check' | 'refresh';
  title: string;
  description: string;
}

export interface StatFigure {
  id: string;
  value: string; // "4.8" | "500+" — string so formatting is data, not JSX
  label: string;
}

export interface Testimonial {
  id: string;
  quote: string;
  authorName: string;
  authorLocation: string;
  avatarUrl: string;
  rating: number; // 1–5
}

const img = (id: string, w = 800) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

export const HERO_IMAGE = img('1564501049412-61c2a3083791', 1600);

export const HERO_SOCIAL_PROOF = 'A curated world of distinctive hotels';

/** Destinations for the marquee + future search shortcuts. US-first for launch
 *  (India returns in a later phase — swap this list and the data below). */
export const DESTINATIONS: string[] = [
  'New York',
  'San Francisco',
  'Charleston',
  'Aspen',
  'Napa Valley',
  'Savannah',
  'Austin',
  'Miami',
  'Portland',
  'Santa Fe',
];

export const PILL_CATEGORIES: PillCategory[] = [
  { key: 'all', label: 'All' },
  { key: 'hotel', label: 'Hotel' },
];

export const FEATURED_STAYS: FeaturedStayDTO[] = [
  {
    id: 'the-marlowe-nyc',
    name: 'The Marlowe',
    location: 'New York, New York',
    imageUrl: img('1566073771259-6a8506099945'),
    images: [img('1566073771259-6a8506099945')],
    guests: 4,
    beds: 2,
    baths: 2,
    areaSqft: 1500,
    pricePerNight: 340,
    currency: 'USD',
    rating: 4.9,
    reviewCount: 218,
    category: 'hotel',
  },
  {
    id: 'vine-and-oak-napa',
    name: 'Vine & Oak',
    location: 'Napa Valley, California',
    imageUrl: img('1582719508461-905c673771fd'),
    images: [img('1582719508461-905c673771fd')],
    guests: 5,
    beds: 3,
    baths: 2,
    areaSqft: 1600,
    pricePerNight: 420,
    currency: 'USD',
    rating: 4.8,
    reviewCount: 174,
    category: 'hotel',
  },
  {
    id: 'the-battery-house-charleston',
    name: 'The Battery House',
    location: 'Charleston, South Carolina',
    imageUrl: img('1551882547-ff40c63fe5fa'),
    images: [img('1551882547-ff40c63fe5fa')],
    guests: 6,
    beds: 3,
    baths: 2,
    areaSqft: 1500,
    pricePerNight: 280,
    currency: 'USD',
    rating: 4.6,
    reviewCount: 96,
    category: 'hotel',
  },
  {
    id: 'pinecrest-lodge-aspen',
    name: 'Pinecrest Lodge',
    location: 'Aspen, Colorado',
    imageUrl: img('1520250497591-112f2f40a3f4'),
    images: [img('1520250497591-112f2f40a3f4')],
    guests: 5,
    beds: 3,
    baths: 2,
    areaSqft: 1500,
    pricePerNight: 510,
    currency: 'USD',
    rating: 4.8,
    reviewCount: 132,
    category: 'hotel',
  },
  {
    id: 'marigold-inn-savannah',
    name: 'Marigold Inn',
    location: 'Savannah, Georgia',
    imageUrl: img('1564013799919-ab600027ffc6'),
    images: [img('1564013799919-ab600027ffc6')],
    guests: 5,
    beds: 2,
    baths: 2,
    areaSqft: 1500,
    pricePerNight: 230,
    currency: 'USD',
    rating: 4.7,
    reviewCount: 88,
    category: 'hotel',
  },
  {
    id: 'cliffside-retreat-big-sur',
    name: 'Cliffside Retreat',
    location: 'Big Sur, California',
    imageUrl: img('1542314831-068cd1dbfeeb'),
    images: [img('1542314831-068cd1dbfeeb')],
    guests: 4,
    beds: 2,
    baths: 2,
    areaSqft: 1500,
    pricePerNight: 390,
    currency: 'USD',
    rating: 4.9,
    reviewCount: 203,
    category: 'hotel',
  },
];

export const FEATURED_PROPERTY: FeaturedStayDTO = {
  id: 'the-cedar-lakehouse-tahoe',
  name: 'The Cedar Lakehouse',
  location: 'Lake Tahoe, California',
  imageUrl: img('1564501049412-61c2a3083791', 1400),
  images: [
    img('1564501049412-61c2a3083791', 1400),
    img('1566073771259-6a8506099945', 1400),
    img('1582719508461-905c673771fd', 1400),
    img('1520250497591-112f2f40a3f4', 1400),
    img('1542314831-068cd1dbfeeb', 1400),
  ],
  guests: 4,
  beds: 2,
  baths: 2,
  areaSqft: 1500,
  pricePerNight: 560,
  currency: 'USD',
  rating: 4.9,
  reviewCount: 311,
  category: 'hotel',
};

export const UNIQUE_FEATURES: FeatureItem[] = [
  {
    id: 'handpicked',
    icon: 'sparkles',
    title: 'Hand-picked, never auto-listed',
    description:
      'Every hotel on BestAcco is chosen by a person. We look for places with a point of view: design, heritage, a sense of where they are.',
  },
  {
    id: 'characterful',
    icon: 'badge-dollar',
    title: 'Personality over uniformity',
    description:
      'No identikit rooms. From restored brownstones to coastal inns, each hotel has a story you can feel the moment you walk in.',
  },
  {
    id: 'verified',
    icon: 'shield-check',
    title: 'Verified, end to end',
    description:
      'We visit, photograph and verify each property so what you see is what you stay in. Real rooms, real hosts, no surprises.',
  },
  {
    id: 'direct',
    icon: 'refresh',
    title: 'Book direct',
    description:
      'Reserve in a few taps and deal with the hotel directly, not a faceless call centre. No charge today on most stays.',
  },
];

export const UNIQUE_STATS: StatFigure[] = [
  { id: 'rating', value: '4.8', label: 'Average guest rating' },
  { id: 'hotels', value: '500+', label: 'Hand-picked hotels' },
  { id: 'cities', value: '40+', label: 'US cities' },
];

export const TESTIMONIALS: Testimonial[] = [
  {
    id: 't1',
    quote:
      'Every BestAcco hotel feels chosen, not listed. We found a restored brownstone in Charleston we would never have discovered ourselves. It had more soul than any chain we have ever stayed in.',
    authorName: 'Emily Carter',
    authorLocation: 'Austin, Texas',
    avatarUrl: 'https://i.pravatar.cc/120?img=32',
    rating: 5,
  },
  {
    id: 't2',
    quote:
      'I booked in under two minutes and the hotel sent a warm, personal note the same evening. It felt like being looked after by people who genuinely care about the place.',
    authorName: 'Marcus Bennett',
    authorLocation: 'Chicago, Illinois',
    avatarUrl: 'https://i.pravatar.cc/120?img=12',
    rating: 5,
  },
  {
    id: 't3',
    quote:
      'BestAcco is the only place I browse before a trip. The hotels have a point of view: design, food, a sense of where they are. Nothing here feels generic.',
    authorName: 'Sophia Nguyen',
    authorLocation: 'Seattle, Washington',
    avatarUrl: 'https://i.pravatar.cc/120?img=45',
    rating: 5,
  },
];

// ── First-booking offer ─────────────────────────────────────────────────────

export interface OfferPromo {
  eyebrow: string;
  title: string;
  description: string;
  code: string;
}

export const FIRST_BOOKING_OFFER: OfferPromo = {
  eyebrow: 'Limited time',
  title: 'First booking on BestAcco?',
  description: 'Save 10% on your first stay, applied automatically on bookings over $150.',
  code: 'BESTACCO10',
};

// ── Host CTA ──────────────────────────────────────────────────────────────────

export interface HostCtaContent {
  title: string;
  description: string;
  ctaLabel: string;
}

export const HOST_CTA: HostCtaContent = {
  title: 'Have a hotel with a story to tell?',
  description:
    'List it on BestAcco and reach travellers who value the details. Our team reviews every hotel, then guests book with you direct.',
  ctaLabel: 'List your hotel',
};

// ── For hoteliers (conversion band) ─────────────────────────────────────────

export interface HotelierValue {
  id: string;
  icon: 'coins' | 'compass' | 'camera';
  title: string;
  description: string;
}

export const HOTELIER_VALUE: HotelierValue[] = [
  {
    id: 'direct',
    icon: 'coins',
    title: 'Direct bookings, lower fees',
    description:
      'Guests book you directly. Keep more of every stay instead of handing 15 to 20 percent to a faceless OTA.',
  },
  {
    id: 'audience',
    icon: 'compass',
    title: 'A curated audience',
    description:
      'Reach travellers who choose hotels for their soul, not the cheapest room in a list of ten thousand.',
  },
  {
    id: 'brand',
    icon: 'camera',
    title: 'A brand that flatters you',
    description:
      'Editorial photography and a considered listing make your property look exactly as special as it is.',
  },
];

// ── Hotelier video stories ──────────────────────────────────────────────────

export interface HotelierStory {
  id: string;
  name: string;
  hotel: string;
  location: string;
  quote: string;
  posterUrl: string;
  videoUrl: string; // swap for real clips later
}

// Placeholder clip so the player is demo-able; replace with real hotelier films.
const SAMPLE_CLIP =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4';

export const HOTELIER_STORIES: HotelierStory[] = [
  {
    id: 's1',
    name: 'Hannah Brooks',
    hotel: 'The Battery House',
    location: 'Charleston, South Carolina',
    quote: 'BestAcco filled our shoulder season with exactly the guests we hoped for.',
    posterUrl: img('1582719508461-905c673771fd', 800),
    videoUrl: SAMPLE_CLIP,
  },
  {
    id: 's2',
    name: 'David Cohen',
    hotel: 'The Marlowe',
    location: 'New York, New York',
    quote: 'We went from quiet mid-weeks to steady direct bookings in three months.',
    posterUrl: img('1566073771259-6a8506099945', 800),
    videoUrl: SAMPLE_CLIP,
  },
  {
    id: 's3',
    name: 'Lucia Romano',
    hotel: 'Vine & Oak',
    location: 'Napa Valley, California',
    quote: 'Guests arrive already in love with the place. That changes everything.',
    posterUrl: img('1551882547-ff40c63fe5fa', 800),
    videoUrl: SAMPLE_CLIP,
  },
];
